'use client';
import React, { useState, useEffect } from 'react';
import { entryService, userService } from '@/lib/supabaseService';
import { CLIMBS, type ClimbEntry, type Climb, type UserProfile } from '@/lib/climbData';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';

interface ClimbStat {
  climb: Climb;
  entry: ClimbEntry | null;
  rank: number | null;
  totalParticipants: number;
}

function formatSeconds(s: number): string {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

function DifficultyBadge({ difficulty }: { difficulty: Climb['difficulty'] }) {
  const map = {
    easy: { label: 'Facile', cls: 'bg-green-500/15 text-green-400 border-green-500/30' },
    medium: { label: 'Media', cls: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' },
    hard: { label: 'Difficile', cls: 'bg-orange-500/15 text-orange-400 border-orange-500/30' },
    extreme: { label: 'Estrema', cls: 'bg-red-500/15 text-red-400 border-red-500/30' },
  };
  const { label, cls } = map[difficulty] ?? map.hard;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold border ${cls}`}>
      {label}
    </span>
  );
}

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) return (
    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full rank-gold text-xs font-bold">1</span>
  );
  if (rank === 2) return (
    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full rank-silver text-xs font-bold">2</span>
  );
  if (rank === 3) return (
    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full rank-bronze text-xs font-bold">3</span>
  );
  return (
    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-muted text-muted-foreground text-xs font-bold">{rank}</span>
  );
}

export default function DashboardPage() {
  const { user: authUser, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [allEntries, setAllEntries] = useState<ClimbEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'all' | 'completed' | 'missing'>('all');

  useEffect(() => {
    if (authLoading) return;
    async function load() {
      try {
        const [entries, users] = await Promise.all([
          entryService.getAll(),
          userService.getAll(),
        ]);
        setAllEntries(entries);
        if (authUser) {
          const found = users.find((u) => u.id === authUser.id) ?? null;
          setProfile(found);
        } else {
          // Show first active user as demo when not logged in
          const demo = users.find((u) => u.status === 'active' && u.role !== 'admin') ?? null;
          setProfile(demo);
        }
      } catch {
        setAllEntries([]);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [authUser, authLoading]);

  if (loading || authLoading) {
    return (
      <div className="py-8 flex items-center justify-center min-h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="py-24 flex flex-col items-center justify-center gap-4">
        <Icon name="UserCircleIcon" size={56} className="text-muted-foreground" />
        <h1 className="text-2xl font-bold text-foreground">Profilo non trovato</h1>
        <Link href="/sign-up-login-screen" className="btn-primary px-4 py-2 rounded-lg text-sm font-semibold">
          Accedi
        </Link>
      </div>
    );
  }

  const userId = profile.id;

  // Build per-climb stats
  const climbStats: ClimbStat[] = CLIMBS.map((climb) => {
    const allApproved = allEntries.filter(
      (e) => e.climbId === climb.id && e.status === 'approved'
    ).sort((a, b) => a.timeSeconds - b.timeSeconds);

    const userEntry = allApproved.find((e) => e.userId === userId) ?? null;
    const rank = userEntry ? allApproved.findIndex((e) => e.userId === userId) + 1 : null;

    return { climb, entry: userEntry, rank, totalParticipants: allApproved.length };
  });

  const completedClimbs = climbStats.filter((s) => s.entry !== null);
  const podiumFinishes = climbStats.filter((s) => s.rank !== null && s.rank <= 3).length;
  const leaderCount = climbStats.filter((s) => s.rank === 1).length;
  const bestRank = completedClimbs.length > 0
    ? Math.min(...completedClimbs.map((s) => s.rank!))
    : null;

  const avgWpkg =
    completedClimbs.length > 0
      ? completedClimbs.reduce((sum, s) => sum + (s.entry?.wattPerKg ?? 0), 0) / completedClimbs.length
      : 0;

  const totalTimeSeconds = completedClimbs.reduce((sum, s) => sum + (s.entry?.timeSeconds ?? 0), 0);

  const rankHistory = completedClimbs.map((s) => ({
    label: s.climb.name.replace('Passo ', '').replace('Colle ', '').replace('Monte ', ''),
    rank: s.rank!,
  }));

  const filteredStats = climbStats.filter((s) => {
    if (activeTab === 'completed') return s.entry !== null;
    if (activeTab === 'missing') return s.entry === null;
    return true;
  });

  return (
    <div className="py-8 space-y-8 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center text-xl font-bold text-primary border border-primary/30">
            {profile.avatar}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-foreground">{profile.name}</h1>
              {profile.clubVerified && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-green-500/15 text-green-400 border border-green-500/30 text-xs font-semibold">
                  <Icon name="CheckBadgeIcon" size={12} />
                  IA Climb
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Membro dal {new Date(profile.joinedAt).toLocaleDateString('it-IT', { year: 'numeric', month: 'long' })}
              {' · '}
              {profile.authMethod === 'strava' ? (
                <span className="text-orange-400 font-semibold">Strava</span>
              ) : (
                <span className="text-muted-foreground">Email</span>
              )}
            </p>
          </div>
        </div>
        <Link
          href="/"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted border border-border text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors"
        >
          <Icon name="TrophyIcon" size={15} />
          Classifica globale
        </Link>
      </div>

      {/* KPI bento grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="col-span-2 lg:col-span-1 bg-card border border-border rounded-2xl p-5 card-glow flex flex-col gap-1">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Salite completate</p>
          <div className="flex items-end gap-2 mt-1">
            <span className="text-4xl font-bold text-foreground font-mono-nums">{completedClimbs.length}</span>
            <span className="text-lg text-muted-foreground mb-1">/ 9</span>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-primary to-accent transition-all"
              style={{ width: `${(completedClimbs.length / 9) * 100}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1">{Math.round((completedClimbs.length / 9) * 100)}% completato</p>
        </div>

        <div className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-1">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Miglior rank</p>
          <div className="flex items-center gap-2 mt-1">
            <Icon name="TrophyIcon" size={20} className="text-yellow-400" />
            <span className="text-4xl font-bold text-yellow-400 font-mono-nums">
              {bestRank !== null ? `#${bestRank}` : '—'}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-auto">su tutte le salite</p>
        </div>

        <div className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-1">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Podi (top 3)</p>
          <div className="flex items-center gap-2 mt-1">
            <Icon name="StarIcon" size={20} className="text-accent" />
            <span className="text-4xl font-bold text-accent font-mono-nums">{podiumFinishes}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-auto">{leaderCount} volte leader</p>
        </div>

        <div className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-1">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Media W/kg</p>
          <div className="flex items-center gap-2 mt-1">
            <Icon name="BoltIcon" size={20} className="text-primary" />
            <span className="text-4xl font-bold text-foreground font-mono-nums">
              {avgWpkg > 0 ? avgWpkg.toFixed(1) : '—'}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-auto">potenza media</p>
        </div>
      </div>

      {/* Rank progression + total time row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-5">
          <h2 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="ChartBarIcon" size={16} className="text-primary" />
            Progressione rank per salita
          </h2>
          {rankHistory.length > 0 ? (
            <div className="flex items-end gap-3 h-28">
              {rankHistory.map((item, i) => {
                const maxRank = 12;
                const barHeight = Math.max(10, Math.round(((maxRank - item.rank + 1) / maxRank) * 100));
                const isTop = item.rank === 1;
                return (
                  <div key={`rank-bar-${i}`} className="flex-1 flex flex-col items-center gap-1.5">
                    <span className={`text-xs font-bold font-mono-nums ${isTop ? 'text-yellow-400' : 'text-muted-foreground'}`}>
                      #{item.rank}
                    </span>
                    <div className="w-full rounded-t-md transition-all" style={{
                      height: `${barHeight}%`,
                      background: isTop
                        ? 'linear-gradient(to top, #f59e0b, #fbbf24)'
                        : 'linear-gradient(to top, hsl(var(--primary)/0.6), hsl(var(--primary)/0.9))',
                    }} />
                    <span className="text-[10px] text-muted-foreground truncate w-full text-center">{item.label}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">Nessuna salita completata ancora</p>
          )}
        </div>

        <div className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-4">
          <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Icon name="ClockIcon" size={16} className="text-primary" />
            Riepilogo tempi
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">Tempo totale</span>
              <span className="font-mono-nums font-bold text-foreground text-sm">{formatSeconds(totalTimeSeconds)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">Salite completate</span>
              <span className="font-mono-nums font-bold text-foreground text-sm">{completedClimbs.length} / 9</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">Podi conquistati</span>
              <span className="font-mono-nums font-bold text-accent text-sm">{podiumFinishes}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">Volte leader</span>
              <span className="font-mono-nums font-bold text-yellow-400 text-sm">{leaderCount}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">Media W/kg</span>
              <span className="font-mono-nums font-bold text-primary text-sm">{avgWpkg > 0 ? avgWpkg.toFixed(2) : '—'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Per-climb PR table */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Icon name="FlagIcon" size={16} className="text-primary" />
            PR personali — tutte le 9 salite
          </h2>
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            {(['all', 'completed', 'missing'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                  activeTab === tab
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab === 'all' ? 'Tutte' : tab === 'completed' ? 'Completate' : 'Mancanti'}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="px-5 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Salita</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Difficoltà</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">PR Tempo</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Rank</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Partecipanti</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">W/kg</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Data</th>
              </tr>
            </thead>
            <tbody>
              {filteredStats.map((stat) => (
                <tr key={`dash-row-${stat.climb.id}`} className="border-b border-border/50 row-hover transition-all">
                  <td className="px-5 py-4">
                    <div>
                      <p className="font-semibold text-foreground">{stat.climb.name}</p>
                      <p className="text-xs text-muted-foreground">{stat.climb.location} · {stat.climb.distance} km · {stat.climb.elevation} m</p>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <DifficultyBadge difficulty={stat.climb.difficulty} />
                  </td>
                  <td className="px-4 py-4">
                    {stat.entry ? (
                      <span className="font-mono-nums font-bold text-accent text-base">{stat.entry.timeFormatted}</span>
                    ) : (
                      <span className="text-muted-foreground text-sm italic">Non completata</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    {stat.rank !== null ? (
                      <RankBadge rank={stat.rank} />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm text-muted-foreground font-mono-nums">{stat.totalParticipants}</span>
                  </td>
                  <td className="px-4 py-4">
                    {stat.entry ? (
                      <span className="font-mono-nums text-sm text-foreground">{stat.entry.wattPerKg.toFixed(1)}</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    {stat.entry ? (
                      <span className="text-sm text-muted-foreground font-mono-nums">{stat.entry.date}</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-3 border-t border-border flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            {completedClimbs.length} di 9 salite completate
          </p>
          <p className="text-xs text-muted-foreground">
            Solo tempi approvati
          </p>
        </div>
      </div>
    </div>
  );
}
